import { aiChat } from './AiBackend';
import { BusyIndicator } from './BusyIndicator';
import { MessageList, MessageDto } from './MessageList';
import { ObjectSelection } from './ObjectSelection';
import { getXeokit } from './XeokitSingleton';

async function talkToAiBackend(messages: MessageDto[], selectedObjectId: string | null, onUpdate: (responsePart: string) => void, onAction?: (action: any) => void) {
    const response = await aiChat(messages, selectedObjectId);

    console.log('AI Response received:', response);

    // Check if response contains actions
    const actionMatch = response.match(/__ACTION__(.*?)__ACTION__/);
    if (actionMatch) {
        try {
            const actionData = JSON.parse(actionMatch[1]);
            console.log('Action data parsed:', actionData);
            onUpdate(actionData.message);
            if (onAction && actionData.action) {
                console.log('Calling onAction with:', actionData.action);
                onAction(actionData.action);
            }
        } catch (e) {
            console.error('Failed to parse action data:', e);
            onUpdate(response);
        }
    } else {
        console.log('No action found in response');
        onUpdate(response);
    }
}

export class AiMessageHandler {
    private messageList: MessageList;
    private busyIndicator: BusyIndicator;
    private objectSelection: ObjectSelection;

    constructor(messageList: MessageList, busyIndicator: BusyIndicator, objectSelection: ObjectSelection) {
        this.messageList = messageList;
        this.messageList.subscribe(this.handleNewMessage.bind(this)); // Subscribe to the message list

        this.busyIndicator = busyIndicator;
        this.objectSelection = objectSelection;
    }

    // Handle new messages from the message list
    private async handleNewMessage(messages: MessageDto[]) {
        const lastMessage = messages[messages.length - 1];

        // If the last message is from the user, initiate AI response
        if (lastMessage?.who === 'user') {
            this.busyIndicator.setBusy(true);
            this.injectEmptySystemMessage(); // Insert a placeholder message for AI response
            await this.handleAiResponse(); // Talk to AI and stream the response
            this.busyIndicator.setBusy(false);
        }
    }

    // Inject an empty message to indicate the AI is "thinking"
    private injectEmptySystemMessage() {
        this.messageList.addMessage({
            id: `system-${Date.now()}`, // Generate a unique ID
            text: '',
            who: 'system',
        });
    }

    // Handle the AI response, streaming it to the MessageList
    private async handleAiResponse() {
        const selectedObject = this.objectSelection.getSelectedObject();
        const selectedObjectId = selectedObject ? String(selectedObject.id) : null;

        await talkToAiBackend(
            this.messageList.getMessages().slice(0, -1),
            selectedObjectId,
            (responsePart: string) => {
                this.messageList.updateLastMessage(responsePart); // Stream the AI response
            },
            (action: any) => {
                this.handleAction(action); // Handle actions from AI
            }
        );
    }

    // Handle actions returned by the AI
    private handleAction(action: any) {
        console.log('handleAction called with:', action);
        const viewer = getXeokit();
        if (!viewer) {
            console.error('Xeokit viewer not available');
            return;
        }

        switch (action.type) {
            case 'isolate_object':
                console.log('Calling isolateObject with:', action.objectId);
                this.isolateObject(action.objectId);
                break;
            case 'isolate_objects':
                console.log('Calling isolateObjects with:', action.objectIds);
                this.isolateObjects(action.objectIds, action.reason);
                break;
            case 'restore_objects':
                console.log('Calling restoreAllObjects');
                this.restoreAllObjects();
                break;
            default:
                console.warn('Unknown action type:', action.type);
        }
    }

    // Isolate a specific object in the viewer
    private isolateObject(objectId: string) {
        const viewer = getXeokit();
        if (!viewer) {
            console.error('Xeokit viewer not available');
            return;
        }

        try {
            console.log(`Isolating object: ${objectId}`);

            // Map numeric ID to viewer object ID
            const viewerObjectIds = this.mapNumericIdsToViewerIds([objectId]);
            const viewerObjectId = viewerObjectIds[0];

            console.log(`Mapped to viewer ID: ${viewerObjectId}`);

            // Hide all objects first
            viewer.scene.setObjectsVisible(viewer.scene.objectIds, false);

            // Show only the target object
            viewer.scene.setObjectsVisible([viewerObjectId], true);

            // Fly to the object
            viewer.cameraFlight.flyTo({
                aabb: viewer.scene.getAABB([viewerObjectId])
            });

            console.log(`Successfully isolated object ${viewerObjectId}`);
        } catch (error) {
            console.error('Error isolating object:', error);
        }
    }

    // Map numeric IDs to viewer object IDs
    private mapNumericIdsToViewerIds(numericIds: string[]): string[] {
        const viewer = getXeokit();
        if (!viewer) {
            console.error('Xeokit viewer not available for ID mapping');
            return numericIds; // Return original IDs as fallback
        }

        const mappedIds: string[] = [];

        for (const numericId of numericIds) {
            console.log(`Mapping numeric ID: ${numericId}`);

            // Search through all scene object IDs to find one containing this numeric ID
            const matchingId = viewer.scene.objectIds.find(sceneId => {
                // Check if scene ID contains the numeric ID in brackets: "Type[493697]"
                return sceneId.includes(`[${numericId}]`);
            });

            if (matchingId) {
                console.log(`Found viewer ID: ${matchingId} for numeric ID: ${numericId}`);
                mappedIds.push(matchingId);
            } else {
                console.log(`No viewer ID found for numeric ID: ${numericId}, using as-is`);
                mappedIds.push(numericId); // Fallback to original ID
            }
        }

        return mappedIds;
    }

    // Isolate multiple objects
    private isolateObjects(objectIds: string[], reason?: string) {
        const viewer = getXeokit();
        if (!viewer) {
            console.error('Xeokit viewer not available');
            return;
        }

        try {
            console.log(`Isolating ${objectIds.length} objects:`, objectIds);
            if (reason) {
                console.log(`Reason: ${reason}`);
            }

            // Map numeric IDs to viewer object IDs
            const viewerObjectIds = this.mapNumericIdsToViewerIds(objectIds);
            console.log(`Mapped to viewer IDs:`, viewerObjectIds);

            // Hide all objects first
            viewer.scene.setObjectsVisible(viewer.scene.objectIds, false);

            // Show only the target objects
            viewer.scene.setObjectsVisible(viewerObjectIds, true);

            // Fly camera to show the isolated objects
            viewer.cameraFlight.flyTo({
                aabb: viewer.scene.getAABB(viewerObjectIds)
            });

            console.log(`Successfully isolated ${viewerObjectIds.length} objects`);
        } catch (error) {
            console.error('Error isolating objects:', error);
        }
    }

    // Restore all objects visibility
    private restoreAllObjects() {
        const viewer = getXeokit();
        if (!viewer) {
            console.error('Xeokit viewer not available');
            return;
        }

        try {
            console.log('Restoring all objects visibility');

            // Show all objects
            viewer.scene.setObjectsVisible(viewer.scene.objectIds, true);

            // Reset camera to show all
            viewer.cameraFlight.flyTo({
                aabb: viewer.scene.getAABB(viewer.scene.objectIds)
            });

            console.log('Successfully restored all objects');
        } catch (error) {
            console.error('Error restoring objects:', error);
        }
    }

    // Clean up subscriptions when necessary
    public cleanup() {
        this.messageList.unsubscribe(this.handleNewMessage.bind(this)); // Unsubscribe from message list
    }
}
